// Coûts de la propreté : par tournée ou par prestataire sur une période, nature et montant en francs CFA,
// totaux par nature et par mois ; saisie déclarative par le responsable technique.
import { CODES_NATURES_COUT, NATURES_COUT, schemaCreationCout } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, Filtres, LienBouton, Selection, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Coins, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerCout, INVALIDER_PROPRETE, useCouts, usePrestataires, useTournees } from './api';
import { aujourdhuiIso, montantFrancs } from './logique';
import type { Cout, NatureCout } from './types';
import styles from './proprete.module.css';

function FormulaireCout({ onFermer }: { onFermer: () => void }) {
  const tournees = useTournees();
  const prestataires = usePrestataires();
  const [v, setV] = useState({ rattachement: 'tournee' as 'tournee' | 'prestataire', tournee_id: '', prestataire_id: '', debut: aujourdhuiIso(), fin: aujourdhuiIso(), nature: 'carburant' as NatureCout, libelle: '', montant: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationCout.safeParse({
        tournee_id: v.rattachement === 'tournee' ? v.tournee_id || undefined : undefined, prestataire_id: v.rattachement === 'prestataire' ? v.prestataire_id || undefined : undefined,
        periode_debut: v.debut, periode_fin: v.fin, nature: v.nature, libelle: v.libelle, montant: Number(v.montant),
      });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0] ?? 'rattachement'), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerCout(corps.data);
    },
    succes: (c) => `Coût enregistré : ${c.libelle}, ${montantFrancs(c.montant)}.`,
    invalider: INVALIDER_PROPRETE,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue ouvert titre="Nouveau coût" description="Montant en francs CFA, entier. Le coût se rattache à une tournée ou à un prestataire." onFermer={onFermer} actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Enregistrer le coût</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Rattachement" options={[{ valeur: 'tournee', libelle: 'Une tournée' }, { valeur: 'prestataire', libelle: 'Un prestataire' }]} value={v.rattachement} onChange={(e) => setV((x) => ({ ...x, rattachement: e.target.value as 'tournee' | 'prestataire' }))} />
        {v.rattachement === 'tournee' ? (
          <Selection libelle="Tournée" options={(tournees.data ?? []).map((t) => ({ valeur: t.id, libelle: `${t.code} · ${t.nom}` }))} optionVide="Choisir une tournée" value={v.tournee_id} onChange={(e) => setV((x) => ({ ...x, tournee_id: e.target.value }))} erreur={champ('tournee_id') ?? champ('rattachement')} required />
        ) : (
          <Selection libelle="Prestataire" options={(prestataires.data ?? []).map((p) => ({ valeur: p.id, libelle: p.nom }))} optionVide="Choisir un prestataire" value={v.prestataire_id} onChange={(e) => setV((x) => ({ ...x, prestataire_id: e.target.value }))} erreur={champ('prestataire_id') ?? champ('rattachement')} required />
        )}
        <Champ libelle="Début de la période" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} erreur={champ('periode_debut')} required />
        <Champ libelle="Fin de la période" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} erreur={champ('periode_fin')} required />
        <Selection libelle="Nature" options={CODES_NATURES_COUT.map((n) => ({ valeur: n, libelle: NATURES_COUT[n] }))} value={v.nature} onChange={(e) => setV((x) => ({ ...x, nature: e.target.value as NatureCout }))} />
        <Champ libelle="Montant (F)" type="number" value={v.montant} onChange={(e) => setV((x) => ({ ...x, montant: e.target.value }))} erreur={champ('montant')} required />
      </div>
      <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} erreur={champ('libelle')} required />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageCouts() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('proprete-couts', { nature: '' });
  const parametres: Record<string, string> = { limite: '200' };
  if (filtres.nature) parametres.nature = filtres.nature;
  const couts = useCouts(parametres);
  const [creation, setCreation] = useState(false);
  const colonnes: ColonneTableau<Cout>[] = [
    { id: 'periode', entete: 'Période', enTeteLigne: true, masquable: false, cellule: (c) => `${date(c.periode.debut)} au ${date(c.periode.fin)}`, valeurTri: (c) => c.periode.debut },
    { id: 'rattachement', entete: 'Rattachement', cellule: (c) => c.tournee?.nom ?? c.prestataire?.nom ?? '' },
    { id: 'nature', entete: 'Nature', cellule: (c) => c.nature_libelle, valeurTri: (c) => c.nature },
    { id: 'libelle', entete: 'Libellé', cellule: (c) => c.libelle },
    { id: 'montant', entete: 'Montant', numerique: true, cellule: (c) => montantFrancs(c.montant), valeurTri: (c) => c.montant },
    { id: 'piece', entete: 'Pièce', cellule: (c) => c.piece?.titre ?? 'aucune' },
  ];
  const totaux = couts.data?.totaux;
  return (
    <>
      <EnTetePage etiquette="Déchets et propreté" titre="Coûts de la propreté" description="Coûts déclarés par tournée ou par prestataire : carburant, entretien, prestations, traitement ; aucun montant n'est calculé par la plateforme."
        actions={<><LienBouton href="/proprete" icone={ArrowLeft} variante="secondaire">Tournées</LienBouton>{peut(contexte, 'dechets', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouveau coût</Bouton> : null}</>} />
      <Filtres groupes={[{ id: 'nature', libelle: 'Nature', options: [{ valeur: '', libelle: 'Toutes' }, ...CODES_NATURES_COUT.map((n) => ({ valeur: n, libelle: NATURES_COUT[n] }))] }]} valeurs={filtres} onChanger={setFiltres} />
      {totaux ? (
        <dl className={styles.totaux} aria-label="Totaux">
          <div><dt>Total</dt><dd data-testid="total-couts">{montantFrancs(totaux.total)}</dd></div>
          {totaux.par_nature.map((n) => (
            <div key={n.nature}><dt>{NATURES_COUT[n.nature as NatureCout] ?? n.nature}</dt><dd>{montantFrancs(n.total)}</dd></div>
          ))}
        </dl>
      ) : null}
      <EtatRequete chargement={couts.isPending} erreur={couts.error} onReessayer={() => void couts.refetch()} lot="L3-B" fonction="La liste des coûts">
        {(couts.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucun coût" icone={Coins}><p>Aucun coût n&apos;est enregistré pour ce filtre.</p></EtatVide> : <Tableau legende="Coûts" colonnes={colonnes} lignes={couts.data?.elements ?? []} cleLigne={(c) => c.id} cleColonnes="proprete-couts" />}
      </EtatRequete>
      {creation ? <FormulaireCout onFermer={() => setCreation(false)} /> : null}
    </>
  );
}
